import React from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "../UIElements/Icon";
import { convertTime, useGlobalUtils } from "../../../lib/globalUtils";

const AiPerformanceCards = ({ data, isLoading, error }) => {
  const { t } = useTranslation("overview");
  const { parsePrice } = useGlobalUtils();

  // Helper function to format change percentage
  const formatChangePercentage = (change) => {
    if (change === null || change === undefined) return null;
    const isPositive = change > 0;
    const isZero = change === 0;
    return {
      value: `${isPositive ? "+" : ""}${change.toFixed(1)}%`,
      isPositive,
      isZero,
    };
  };

  // Helper function to format hours using globalUtils
  const formatHours = (minutes) => {
    return convertTime(minutes, "min", "min", {
      decimals: 1,
      includeUnit: true,
    });
  };

  // Helper function to format ROI
  const formatROI = (roi) => {
    return `${roi.toFixed(2)}x`;
  };

  // Helper function to format cost using globalUtils
  const formatCost = (cost) => {
    return parsePrice(cost, { decimal: 2 });
  };

  // Helper function to format response time using globalUtils
  const formatResponseTime = (ms) => {
    return convertTime(ms, "ms", "s", { decimals: 1, includeUnit: true });
  };

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-800 bg-gray-900 p-4 sm:p-6">
        <div className="animate-pulse">
          <div className="mb-4 h-5 w-1/3 rounded bg-gray-700 sm:mb-6 sm:h-6"></div>
          <div className="space-y-3 sm:space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="rounded-lg bg-gray-800 p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className="h-8 w-8 rounded-lg bg-gray-700 sm:h-10 sm:w-10"></div>
                    <div className="space-y-1">
                      <div className="h-3 w-20 rounded bg-gray-700 sm:h-4 sm:w-24"></div>
                      <div className="h-2 w-24 rounded bg-gray-700 sm:h-3 sm:w-32"></div>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="h-6 w-12 rounded bg-gray-700 sm:h-8 sm:w-16"></div>
                    <div className="h-2 w-8 rounded bg-gray-700 sm:h-3 sm:w-12"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-gray-800 bg-gray-900 p-4 sm:p-6">
        <div className="text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10 sm:mb-4 sm:h-16 sm:w-16">
            <svg
              className="h-6 w-6 text-red-400 sm:h-8 sm:w-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-base font-medium text-red-400 sm:text-lg">
            {t("performance.error.title")}
          </h3>
          <p className="text-sm text-gray-400">
            {error.message || t("performance.error.subtitle")}
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-lg border border-gray-800 bg-gray-900 p-4 sm:p-6">
        <div className="text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-500/10 sm:mb-4 sm:h-16 sm:w-16">
            <svg
              className="h-6 w-6 text-gray-400 sm:h-8 sm:w-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-base font-medium text-gray-300 sm:text-lg">
            {t("performance.empty.title")}
          </h3>
          <p className="text-sm text-gray-400">
            {t("performance.empty.subtitle")}
          </p>
        </div>
      </div>
    );
  }

  const performanceCards = [
    data.conversion_rate_percentage?.hidden !== true && {
      id: "conversion-rate",
      title: t("performance.cards.conversionRate.title"),
      subtitle: t("performance.yesterdayPrefix", {
        value: `${data.conversion_rate_percentage?.previous?.toFixed(0) ?? 0}%`,
      }),
      value: `${data.conversion_rate_percentage?.current?.toFixed(0) ?? 0}%`,
      unit: t("performance.cards.conversionRate.unit"),
      icon: "BarChart3",
      iconBg: "bg-purple-500",
      iconColor: "text-white",
      valueColor: "text-white",
      change: formatChangePercentage(
        data.conversion_rate_percentage?.change_percentage,
      ),
    },
    data.human_minutes_saved?.hidden !== true && {
      id: "hours-saved",
      title: t("performance.cards.hoursSaved.title"),
      subtitle: t("performance.yesterdayPrefix", {
        value: formatHours(data.human_minutes_saved?.previous ?? 0),
      }),
      value: formatHours(data.human_minutes_saved?.current ?? 0),
      unit: t("performance.cards.hoursSaved.unit"),
      icon: "Bot",
      iconBg: "bg-blue-500",
      iconColor: "text-white",
      valueColor: "text-white",
      change: formatChangePercentage(
        data.human_minutes_saved?.change_percentage,
      ),
    },
    data.average_response_time_ms?.hidden !== true && {
      id: "response-time",
      title: t("performance.cards.responseTime.title"),
      subtitle: t("performance.yesterdayPrefix", {
        value: formatResponseTime(data.average_response_time_ms?.previous ?? 0),
      }),
      value: formatResponseTime(data.average_response_time_ms?.current ?? 0),
      unit: t("performance.cards.responseTime.unit"),
      icon: "Clock",
      iconBg: "bg-orange-500",
      iconColor: "text-white",
      valueColor: "text-white",
      change: formatChangePercentage(
        data.average_response_time_ms?.change_percentage,
      ),
      changeColor:
        data.average_response_time_ms?.change_percentage === 0
          ? "text-yellow-400"
          : data.average_response_time_ms?.change_percentage > 0
            ? "text-red-400"
            : "text-green-400",
    },
    data.average_cost_per_conversation?.hidden !== true && {
      id: "cost-per-conversation",
      title: t("performance.cards.costPerConversation.title"),
      subtitle: t("performance.yesterdayPrefix", {
        value: formatCost(data.average_cost_per_conversation?.previous ?? 0),
      }),
      value: formatCost(data.average_cost_per_conversation?.current ?? 0),
      unit: t("performance.cards.costPerConversation.unit"),
      icon: "DollarSign",
      iconBg: "bg-green-500",
      iconColor: "text-white",
      valueColor: "text-white",
      change: formatChangePercentage(
        data.average_cost_per_conversation?.change_percentage,
      ),
      changeColor:
        data.average_cost_per_conversation?.change_percentage === 0
          ? "text-yellow-400"
          : data.average_cost_per_conversation?.change_percentage > 0
            ? "text-red-400"
            : "text-green-400",
    },
    data.roi_orders_per_dollar?.hidden !== true && {
      id: "roi-orders",
      title: t("performance.cards.roi.title"),
      subtitle: t("performance.yesterdayPrefix", {
        value: formatROI(data.roi_orders_per_dollar?.previous ?? 0),
      }),
      value: formatROI(data.roi_orders_per_dollar?.current ?? 0),
      unit: t("performance.cards.roi.unit"),
      icon: "TrendingUp",
      iconBg: "bg-purple-500",
      iconColor: "text-white",
      valueColor: "text-blue-400",
      change: formatChangePercentage(
        data.roi_orders_per_dollar?.change_percentage,
      ),
      gradient: true,
    },
  ].filter(Boolean);

  return (
    <div className="rounded-lg border border-gray-800 bg-gray-900 p-4 sm:p-6">
      <div className="mb-4 flex items-center justify-between sm:mb-6">
        <div>
          <h3 className="text-base font-semibold text-white sm:text-lg">
            {t("performance.title")}
          </h3>
          <p className="text-xs text-gray-400 sm:text-sm">
            {t("performance.subtitle")}
          </p>
        </div>
      </div>

      <div className="space-y-3 sm:space-y-4">
        {performanceCards.map((card) => (
          <div
            key={card.id}
            className={`flex items-center justify-between rounded-lg border border-gray-700 bg-gray-800 p-3 transition-colors duration-200 hover:border-gray-700 sm:p-4 ${
              card.gradient
                ? "bg-gray-900 bg-gradient-to-r from-purple-900/20 to-purple-800/20"
                : ""
            }`}
          >
            {/* Left side - Icon and Info */}
            <div className="flex min-w-0 flex-1 items-center space-x-2 sm:space-x-3">
              <div
                className={`h-8 w-8 sm:h-10 sm:w-10 ${card.iconBg} flex flex-shrink-0 items-center justify-center rounded-lg`}
              >
                <Icon
                  name={card.icon}
                  className={`h-4 w-4 sm:h-5 sm:w-5 ${card.iconColor}`}
                />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="truncate text-xs font-medium text-white sm:text-sm">
                  {card.title}
                </h4>
                <p className="truncate text-xs text-gray-400">
                  {card.subtitle}
                </p>
              </div>
            </div>

            {/* Right side - Values */}
            <div className="ml-2 flex-shrink-0 text-right">
              <div className="flex flex-col items-end">
                <div className="flex items-baseline space-x-1">
                  <span
                    className={`text-lg font-bold sm:text-2xl ${card.valueColor}`}
                  >
                    {card.value}
                  </span>
                  <span className="hidden text-xs text-gray-400 sm:inline sm:text-sm">
                    {card.unit}
                  </span>
                </div>

                {card.change && (
                  <div className="mt-0.5 flex items-center space-x-1">
                    <span
                      className={`text-xs font-medium ${
                        card.changeColor ??
                        (card.change.isZero
                          ? "text-yellow-400"
                          : card.change.isPositive
                            ? "text-green-400"
                            : "text-red-400")
                      }`}
                    >
                      {card.change.value}
                    </span>
                    <span className="hidden text-xs text-gray-500 sm:inline">
                      {t("performance.comparison")}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AiPerformanceCards;
